/** t331u B11: phone Settings › Clock-in & kiosks (desktop workforce/clock/ClockSettings): geofence per building, kiosks (set up, badges, photos, remove) and recent kiosk activity. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Crosshair, Trash2 } from 'lucide-react';
import { canResetPinToAddress, fencePinEdited, geofencePinNote, RESET_PIN_TO_ADDRESS_LABEL, showResetToAddress, fenceBody, fenceDefaultsBody, fenceDefaultsForm, fenceDefaultsLine, fenceDefaultsOf, fenceDefaultsProblem, fenceDirty, fenceForm, fenceLine, fenceProblem, GEOFENCE_ACCURACY_HINT, GEOFENCE_LAT_PLACEHOLDER, GEOFENCE_LNG_PLACEHOLDER, GEOFENCE_RADIUS_HINT, GEOFENCE_UNKNOWN_NOTE, inheritPlaceholder, type FenceDefaultsForm, type GeofenceDefaults, GEOFENCE_MODES, GEOFENCES_NOTE, KIOSK_LINK_NOTE, KIOSK_PHOTO_NOTE, kioskEventBad, kioskEventRow, kioskLines, KIOSKS_NOTE, mapLink, removedKiosksText, type FenceForm, type Geofence, type Kiosk, type KioskRegistered } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { readPosition, GEO_FAILURE_TEXT } from '@/lib/geo';
import { useWorkspaceId } from '@/lib/hooks';
import { useCanAnywhere, useLocationsWith } from '@/lib/permissions';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Select, Spinner, ask } from '@/components/ui';
import { useBuildingsWith } from '@/features/buildings/common';
import { useWfLookups } from '@/features/workforce/common';
import { QrImage } from '@/features/workforce/clock/QrImage';
import { BottomSheet } from '../BottomSheet';
import { chipBase } from '../RotaSheets';
import { F, ghostBtn, saveBtn } from './PersonSettings';
import { listCard, note, WsAddRow, WsHead, WsRow, WsSwitch } from './WorkspaceSettings';

export function ClockInPage() {
  const fences = useCanAnywhere('workforce.clock.geofence');
  const kiosks = useCanAnywhere('workforce.clock.kiosk');
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-clock-in">
      {fences && <Geofences />}
      {kiosks && <Kiosks />}
      {kiosks && <Activity />}
    </div>
  );
}

function Geofences() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: qk.wf(ws ?? '', 'geofences'), queryFn: () => api.wf.geofences(ws!), enabled: Boolean(ws) });
  const [open, setOpen] = useState<Geofence | null>(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const list = q.data?.geofences ?? [];
  const defaults = q.data?.defaults ?? null;
  return (
    <>
      <WsHead>Geofences</WsHead>
      <div className={listCard} data-testid="m-clock-fences">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !list.length && <p className="px-3 py-3 text-sm text-slate-500">No buildings yet.</p>}
        {defaults && <WsRow testId="m-clock-fence-defaults" title="Defaults" line={fenceDefaultsLine(defaults)} onClick={() => setDefaultsOpen(true)} />}
        {list.map(g => <WsRow key={g.location_id} testId="m-clock-fence" title={g.location_name ?? 'Building'} line={fenceLine(g)} onClick={() => setOpen(g)} />)}
      </div>
      <p className={note}>{GEOFENCES_NOTE}</p>
      {open && <FenceSheet g={open} defaults={defaults} onClose={() => setOpen(null)} />}
      {defaultsOpen && defaults && <FenceDefaultsSheet defaults={defaults} canEdit={q.data?.can_edit_defaults !== false} onClose={() => setDefaultsOpen(false)} />}
    </>
  );
}

/** t467u: workspace defaults for radius and max accuracy. */
function FenceDefaultsSheet({ defaults, canEdit, onClose }: { defaults: GeofenceDefaults; canEdit: boolean; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [f, setF] = useState<FenceDefaultsForm>(() => fenceDefaultsForm(defaults));
  const [busy, setBusy] = useState(false);
  const problem = fenceDefaultsProblem(f);
  const dirty = f.radius.trim() !== String(defaults.radius_m) || f.accuracy.trim() !== String(defaults.max_accuracy_m);
  const save = async () => {
    setBusy(true);
    try { await api.wf.updateGeofenceDefaults(ws!, fenceDefaultsBody(f)); void queryClient.invalidateQueries({ queryKey: qk.wf(ws!, 'geofences') }); toast('Defaults saved', 'success'); onClose(); }
    catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <BottomSheet open onClose={onClose} title="Defaults" testId="m-clock-fence-defaults-sheet"
      footer={canEdit ? <div className="flex px-3 py-2"><button type="button" className={saveBtn} disabled={busy || !dirty || Boolean(problem)} onClick={() => void save()} data-testid="m-clock-fence-defaults-save">Save</button></div> : undefined}>
      <div className="space-y-1 px-4 pb-2">
        <F label="Radius (m)"><Input inputMode="numeric" value={f.radius} disabled={!canEdit} onChange={e => setF({ ...f, radius: e.target.value })} data-testid="m-clock-fence-default-radius" /></F>
        <p className="text-sm text-slate-500">{GEOFENCE_RADIUS_HINT}</p>
        <F label="Max accuracy (m)"><Input inputMode="numeric" value={f.accuracy} disabled={!canEdit} onChange={e => setF({ ...f, accuracy: e.target.value })} data-testid="m-clock-fence-default-accuracy" /></F>
        <p className="text-sm text-slate-500">{GEOFENCE_ACCURACY_HINT}</p>
        <p className="pt-2 text-sm text-slate-500" data-testid="m-clock-fence-unknown-note">Buildings use these unless they have their own value. {GEOFENCE_UNKNOWN_NOTE}</p>
        {problem && <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">{problem}</p>}
      </div>
    </BottomSheet>
  );
}

export function FenceSheet({ g, defaults, onClose }: { g: Geofence; defaults: GeofenceDefaults | null; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const d = fenceDefaultsOf(g, defaults);
  const [f, setF] = useState<FenceForm>(() => fenceForm(g));
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const problem = fenceProblem(f);
  const here = async () => {
    setLocating(true);
    const r = await readPosition(15_000);
    setLocating(false);
    if (!r.reading) { toast(GEO_FAILURE_TEXT[r.failure ?? 'unavailable'], 'error'); return; }
    setF(x => ({ ...x, lat: r.reading!.lat.toFixed(6), lng: r.reading!.lng.toFixed(6) }));
    toast(`Position set (±${r.reading.accuracy_m} m). Save to apply.`, 'success');
  };
  const save = async () => {
    setBusy(true);
    try { await api.wf.updateGeofence(ws!, g.location_id, fenceBody(f)); void queryClient.invalidateQueries({ queryKey: qk.wf(ws!, 'geofences') }); toast(`Geofence saved for ${g.location_name}`, 'success'); onClose(); }
    catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(false); }
  };
  const hasPos = f.lat.trim() !== '' && f.lng.trim() !== '';
  const edited = fencePinEdited(f, g);
  const pinNote = geofencePinNote(g, edited);
  const resetToAddress = async () => {
    if (!canResetPinToAddress(g)) { setF(x => ({ ...x, lat: g.lat?.toString() ?? '', lng: g.lng?.toString() ?? '' })); return; }
    setBusy(true);
    try {
      const r = await api.wf.resetGeofenceToAddress(ws!, g.location_id);
      setF(x => ({ ...x, lat: r.lat?.toString() ?? '', lng: r.lng?.toString() ?? '' }));
      void queryClient.invalidateQueries({ queryKey: qk.wf(ws!, 'geofences') });
      toast(`Pin moved to the address of ${g.location_name}`, 'success');
      onClose();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open onClose={onClose} title={g.location_name ?? 'Geofence'} testId="m-clock-fence-sheet"
      footer={<div className="flex px-3 py-2"><button type="button" className={saveBtn} disabled={busy || !fenceDirty(f, g) || Boolean(problem)} onClick={() => void save()} data-testid="m-clock-fence-save">Save</button></div>}>
      <div className="space-y-1 px-4 pb-2">
        <div className="py-1.5">
          <span className="mb-1 block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">Check</span>
          <div className="flex gap-1.5" role="radiogroup" aria-label="Check" data-testid="m-clock-fence-mode">
            {GEOFENCE_MODES.map(m => <button key={m.id} type="button" role="radio" aria-checked={f.mode === m.id} className={`${chipBase(f.mode === m.id)} h-11 flex-1 justify-center`} onClick={() => setF({ ...f, mode: m.id })}>{m.name}</button>)}
          </div>
          <p className="pt-1 text-sm text-slate-500">{GEOFENCE_MODES.find(m => m.id === f.mode)?.hint}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Latitude"><Input inputMode="decimal" placeholder={GEOFENCE_LAT_PLACEHOLDER} value={f.lat} onChange={e => setF({ ...f, lat: e.target.value })} data-testid="m-clock-fence-lat" /></F>
          <F label="Longitude"><Input inputMode="decimal" placeholder={GEOFENCE_LNG_PLACEHOLDER} value={f.lng} onChange={e => setF({ ...f, lng: e.target.value })} data-testid="m-clock-fence-lng" /></F>
        </div>
        {pinNote && <p className="text-sm text-slate-500" data-testid="m-clock-fence-pin-note">{pinNote}</p>}
        <div className="flex flex-wrap items-center gap-3 py-1">
          <button type="button" className={`${ghostBtn} inline-flex items-center gap-2`} disabled={locating} onClick={() => void here()} data-testid="m-clock-fence-here"><Crosshair className="h-5 w-5" />{locating ? 'Finding you…' : 'Use my position'}</button>
          {showResetToAddress(g, edited) && <button type="button" className={ghostBtn} disabled={busy} onClick={() => void resetToAddress()} data-testid="m-clock-fence-reset-address">{RESET_PIN_TO_ADDRESS_LABEL}</button>}
          {hasPos && <a className="text-sm underline" href={mapLink(f.lat, f.lng)} target="_blank" rel="noreferrer">Check on a map</a>}
        </div>
        <F label="Radius (m)"><Input inputMode="numeric" value={f.radius} placeholder={inheritPlaceholder(d.radius_m)} onChange={e => setF({ ...f, radius: e.target.value })} data-testid="m-clock-fence-radius" /></F>
        <p className="text-sm text-slate-500">{GEOFENCE_RADIUS_HINT}{f.radius.trim() !== '' && <> <button type="button" className="underline" onClick={() => setF({ ...f, radius: '' })} data-testid="m-clock-fence-reset-radius">Use default ({d.radius_m} m)</button></>}</p>
        <F label="Max accuracy (m)"><Input inputMode="numeric" value={f.accuracy} placeholder={inheritPlaceholder(d.max_accuracy_m)} onChange={e => setF({ ...f, accuracy: e.target.value })} data-testid="m-clock-fence-accuracy" /></F>
        <p className="text-sm text-slate-500">{GEOFENCE_ACCURACY_HINT}{f.accuracy.trim() !== '' && <> <button type="button" className="underline" onClick={() => setF({ ...f, accuracy: '' })} data-testid="m-clock-fence-reset-accuracy">Use default ({d.max_accuracy_m} m)</button></>}</p>
        {problem && <p className="text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid="m-clock-fence-problem">{problem}</p>}
      </div>
    </BottomSheet>
  );
}

function Kiosks() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: qk.wf(ws ?? '', 'kiosks'), queryFn: () => api.wf.kiosks(ws!), enabled: Boolean(ws) });
  const [open, setOpen] = useState<Kiosk | null>(null);
  const [adding, setAdding] = useState(false);
  const [reg, setReg] = useState<KioskRegistered | null>(null);
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: qk.wf(ws!, 'kiosks') }); void queryClient.invalidateQueries({ queryKey: qk.wf(ws!, 'kiosk-events') }); };
  const live = q.data?.kiosks.filter(k => !k.revoked_at) ?? [];
  const gone = q.data?.kiosks.filter(k => k.revoked_at) ?? [];
  const cur = open && live.find(k => k.id === open.id);
  const patch = async (k: Kiosk, b: { photo_mode?: 'off' | 'on'; allow_qr?: boolean }) => {
    try { await api.wf.updateKiosk(ws!, k.id, b); refresh(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const revoke = async (k: Kiosk) => {
    if (!await ask({ title: `Remove “${k.name}”?`, body: 'The tablet stops working straight away.', confirmLabel: 'Remove', danger: true })) return;
    try { await api.wf.revokeKiosk(ws!, k.id); refresh(); toast('Kiosk removed', 'success'); setOpen(null); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <>
      <WsHead>Kiosks</WsHead>
      <div className={listCard} data-testid="m-clock-kiosks">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !live.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No kiosks yet.</p>}
        {live.map(k => { const [a, b] = kioskLines(k); return <WsRow key={k.id} testId="m-clock-kiosk" title={k.name} line={<>{a}<br />{b}</>} onClick={() => setOpen(k)} />; })}
        <WsAddRow label="Set up a kiosk" onClick={() => setAdding(true)} testId="m-clock-kiosk-add" />
      </div>
      <p className={note}>{KIOSKS_NOTE}{gone.length ? ` ${removedKiosksText(gone)}.` : ''}</p>
      {cur && <BottomSheet open onClose={() => setOpen(null)} title={cur.name} testId="m-clock-kiosk-sheet"
        footer={<div className="flex px-3 py-2"><button type="button" className={`${ghostBtn} inline-flex flex-1 items-center justify-center gap-2 text-rose-700 dark:text-rose-300`} onClick={() => void revoke(cur)} data-testid="m-clock-kiosk-remove"><Trash2 className="h-5 w-5" />Remove kiosk</button></div>}>
        <div className="px-4 pb-2">
          <p className="pb-1 text-sm text-slate-500">{kioskLines(cur)[0]} · …{cur.token_hint}</p>
          <WsSwitch label="Allow badge QR codes as well as PINs" checked={cur.allow_qr} onChange={v => void patch(cur, { allow_qr: v })} testId="m-clock-kiosk-qr" />
          <WsSwitch label="Take a photo at each clock-in and out" checked={cur.photo_mode === 'on'} onChange={v => void patch(cur, { photo_mode: v ? 'on' : 'off' })} testId="m-clock-kiosk-photo" />
          <p className="pb-1 text-sm text-slate-500">Changes save straight away. {KIOSK_PHOTO_NOTE}</p>
        </div>
      </BottomSheet>}
      {adding && <RegisterSheet onClose={() => setAdding(false)} onDone={r => { setAdding(false); setReg(r); refresh(); }} />}
      {reg && <LinkSheet reg={reg} onClose={() => setReg(null)} />}
    </>
  );
}

function RegisterSheet({ onClose, onDone }: { onClose: () => void; onDone: (r: KioskRegistered) => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const kioskAt = useBuildingsWith('kiosk');
  const at = useLocationsWith('workforce.clock.kiosk');
  const locations = useWfLookups().locations.filter(l => (!kioskAt || kioskAt.has(l.id)) && (at === 'all' || at.includes(l.id)));
  const [f, setF] = useState({ location_id: '', name: '', photo_mode: 'off' as 'off' | 'on', allow_qr: true });
  const [busy, setBusy] = useState(false);
  const loc = f.location_id || locations[0]?.id || '';
  const submit = async () => {
    setBusy(true);
    try { onDone(await api.wf.registerKiosk(ws!, { ...f, name: f.name.trim(), location_id: loc })); }
    catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <BottomSheet open onClose={onClose} title="Set up a kiosk" testId="m-clock-register"
      footer={<div className="flex px-3 py-2"><button type="button" className={saveBtn} disabled={busy || !f.name.trim() || !loc} onClick={() => void submit()} data-testid="m-clock-register-go">Create kiosk link</button></div>}>
      <div className="space-y-1 px-4 pb-2">
        {locations.length ? <F label="Building"><Select value={loc} onChange={e => setF({ ...f, location_id: e.target.value })} data-testid="m-clock-register-building">{locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></F>
          : <p className="py-2 text-sm text-slate-500">None of your buildings can have a kiosk.</p>}
        <F label="Name"><Input placeholder="e.g. Goods-in tablet" maxLength={80} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="m-clock-register-name" /></F>
        <WsSwitch label="Allow badge QR codes as well as PINs" checked={f.allow_qr} onChange={allow_qr => setF({ ...f, allow_qr })} testId="m-clock-register-qr" />
        <WsSwitch label="Take a photo at each clock-in and out" checked={f.photo_mode === 'on'} onChange={v => setF({ ...f, photo_mode: v ? 'on' : 'off' })} testId="m-clock-register-photo" />
        <p className="pb-1 text-sm text-slate-500">{KIOSK_PHOTO_NOTE}</p>
      </div>
    </BottomSheet>
  );
}

function LinkSheet({ reg, onClose }: { reg: KioskRegistered; onClose: () => void }) {
  const toast = useUi(s => s.toast);
  const url = `${window.location.origin}${reg.url}`;
  return (
    <BottomSheet open onClose={onClose} title={`Kiosk link for ${reg.kiosk.name}`} testId="m-clock-link"
      footer={<div className="flex px-3 py-2"><button type="button" className={saveBtn} onClick={onClose}>Done</button></div>}>
      <div className="space-y-3 px-4 pb-2">
        <p className="text-sm">Open this link once on the tablet at <b>{reg.kiosk.location_name}</b>. It stays signed in as a kiosk (not as you) until you remove it.</p>
        <div className="flex justify-center"><QrImage value={url} size={176} label="Kiosk set-up link" /></div>
        <button type="button" className={`${ghostBtn} inline-flex w-full items-center justify-center gap-2`} onClick={() => { void navigator.clipboard?.writeText(url); toast('Link copied', 'success'); }}><Copy className="h-5 w-5" />Copy link</button>
        <p className="text-sm text-slate-600 dark:text-slate-300">{KIOSK_LINK_NOTE}</p>
      </div>
    </BottomSheet>
  );
}

function Activity() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: qk.wf(ws ?? '', 'kiosk-events'), queryFn: () => api.wf.kioskEvents(ws!, { limit: 50 }), enabled: Boolean(ws), refetchInterval: 30_000 });
  const [all, setAll] = useState(false);
  const events = q.data?.events ?? [];
  const unlock = async (userId: string) => {
    try { await api.wf.unlockKioskUser(ws!, userId); toast('Unlocked - they can use their PIN again', 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <>
      <WsHead>Kiosk activity</WsHead>
      <div className={listCard} data-testid="m-clock-activity">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !events.length && <p className="px-3 py-3 text-sm text-slate-500">Nothing yet.</p>}
        {(all ? events : events.slice(0, 8)).map(e => {
          const row = kioskEventRow(e);
          return <WsRow key={e.id} testId="m-clock-event" title={<span className={kioskEventBad(e) ? 'text-rose-700 dark:text-rose-300' : ''}>{row.title}</span>} line={row.line}
            right={e.action === 'user_locked' && e.user_id ? <button type="button" className={`${chipBase(false)} mr-2 h-11`} onClick={() => void unlock(e.user_id!)}>Unlock</button> : undefined} />;
        })}
        {!all && events.length > 8 && <button type="button" className="h-12 w-full text-base font-medium" onClick={() => setAll(true)} data-testid="m-clock-activity-more">Show all {events.length}</button>}
      </div>
    </>
  );
}
